/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  Recording,
  Asset,
  XError,
  TimeUtils,
  BadgeType,
  Ducks,
  MenuUiArea,
  MenuItem,
  MenuItemType,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, call, takeEvery } from 'redux-saga/effects';
import { Selectors as WishlistSelectors } from 'core/store/ducks/wishlist';
import { Selectors as EntitlesAssetsSelectors } from 'core/store/ducks/entitlements/assets/entitledAssets';
import { StoreState } from 'store/store';
import translate from 'services/i18n/translate';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import { Selectors as MyTvRecordingsSelectors, waitForRecordings } from './myTvRecordings';

enum RecordingsKeys {
  thisWeek = 'thisWeek',
  lastWeek = 'lastWeek',
  thisMonth = 'thisMonth',
  lastMonth = 'lastMonth',
  twoMonthsAgo = 'twoMonthsAgo',
  threeMonthsAgo = 'threeMonthsAgo',
  older = 'older',
}

export enum ActionType {
  GET_SECOND_LEVEL_ASSETS = 'myTV/secondLevel/GET_SECOND_LEVEL_ASSETS',
  GET_SECOND_LEVEL_ASSETS_SUCCESS = 'myTV/secondLevel/GET_SECOND_LEVEL_ASSETS_SUCCESS',
  GET_SECOND_LEVEL_ASSETS_FAIL = 'myTV/secondLevel/GET_SECOND_LEVEL_ASSETS_FAIL',
}

interface GetSecondLevelAssetsMeta {
  menuItemId: number;
}

interface GetSecondLevelAssetsPayload {
  data: SecondLevelRails[];
  menuItemId: number;
}

type GetSecondLevelAssetsRequest = ActionRequest<ActionType.GET_SECOND_LEVEL_ASSETS, GetSecondLevelAssetsMeta>;
type GetSecondLevelAssetsSuccess = ActionSuccess<
  ActionType.GET_SECOND_LEVEL_ASSETS_SUCCESS,
  GetSecondLevelAssetsPayload
>;
type GetSecondLevelAssetsFail = ActionFail<ActionType.GET_SECOND_LEVEL_ASSETS_FAIL, GetSecondLevelAssetsMeta>;

export type Actions = GetSecondLevelAssetsRequest | GetSecondLevelAssetsSuccess | GetSecondLevelAssetsFail;

const Actions = {
  getSecondLevelAssets: (menuItemId: number): GetSecondLevelAssetsRequest => ({
    type: ActionType.GET_SECOND_LEVEL_ASSETS,
    meta: {
      menuItemId,
    },
  }),
  getSecondLevelAssetsSuccess: (
    secondLevelRails: SecondLevelRails[],
    menuItemId: number
  ): GetSecondLevelAssetsSuccess => ({
    type: ActionType.GET_SECOND_LEVEL_ASSETS_SUCCESS,
    payload: {
      data: secondLevelRails,
      menuItemId,
    },
  }),
  getSecondLevelAssetsFail: (menuItemId: number, error: XError): GetSecondLevelAssetsFail => ({
    type: ActionType.GET_SECOND_LEVEL_ASSETS_FAIL,
    error,
    meta: { menuItemId },
  }),
};

export const ActionCreators = {
  getSecondLevelAssets: (menuItemId: number): GetSecondLevelAssetsRequest => Actions.getSecondLevelAssets(menuItemId),
};

export interface SecondLevelRails {
  railName: string;
  railId: string;
  menuItemId: number;
  assets: Asset[];
}

export interface ReducerState {
  menuItemId?: number;
  data: SecondLevelRails[];
}

const initialState: ReducerState = {
  data: [],
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_SECOND_LEVEL_ASSETS_SUCCESS:
      return {
        data: action.payload.data,
        menuItemId: action.payload.menuItemId,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getSecondLevelAssets: (state: StoreState): SecondLevelRails[] | undefined => state.myTv.secondLevelRails?.data,
  getSecondLevelMenuItemId: (state: StoreState): number | undefined => state.myTv.secondLevelRails?.menuItemId,
};

/* Helping functions */

function divideByGenre(assets: Asset[], menuItemId: number): SecondLevelRails[] {
  const genreMap = assets?.reduce((acc: { [key: string]: Asset[] }, obj: Asset) => {
    const { genres } = obj;

    genres.forEach((e: string) => {
      if (!acc[e]) {
        acc[e] = [];
      }

      acc[e].push(obj);
    });

    return acc;
  }, {});

  if (genreMap) {
    const secondLevel = Object.keys(genreMap).reduce((acc: SecondLevelRails[], key: string) => {
      const secondLevelRail: SecondLevelRails = {
        railId: key,
        menuItemId,
        railName: key, // Note: rail name and rail id should not be the same, it seems thar we are not internationalizing the genres
        assets: genreMap[key],
      };
      acc.push(secondLevelRail);
      return acc;
    }, []);

    const sortedSecondLevel = secondLevel.sort((a, b) => a.railName.localeCompare(b.railName));

    return sortedSecondLevel;
  }

  return [];
}

function hasAssets(item: SecondLevelRails) {
  return item.assets.length > 0;
}

function getAllRecordingsMap(recordings: Recording[], menuItemId: number): SecondLevelRails[] {
  const timeRecordingsMap = new Map<string, Recording[]>();

  // Getting the diferent times to be used on the recordings sorting
  const nowDate = TimeUtils.now();
  const thisWeekMondayDate = TimeUtils.getLastWeekDayDate(TimeUtils.Weekdays.MONDAY, false, nowDate);
  const lastWeekMondayDate = TimeUtils.getLastWeekDayDate(TimeUtils.Weekdays.MONDAY, true, thisWeekMondayDate);

  const thisMonthDate = TimeUtils.getMonthStartDate(nowDate);
  const lastMonthDate = TimeUtils.getMonthStartDate(nowDate, -1);
  const twoMonthsAgoDate = TimeUtils.getMonthStartDate(nowDate, -2);
  const threeMonthsAgoMonthDate = TimeUtils.getMonthStartDate(nowDate, -3);

  // Init the map with all the correct labels following the desired order
  timeRecordingsMap.set(RecordingsKeys.thisWeek, []);
  timeRecordingsMap.set(RecordingsKeys.lastWeek, []);
  timeRecordingsMap.set(RecordingsKeys.thisMonth, []);
  timeRecordingsMap.set(RecordingsKeys.lastMonth, []);
  timeRecordingsMap.set(RecordingsKeys.twoMonthsAgo, []);
  timeRecordingsMap.set(RecordingsKeys.threeMonthsAgo, []);
  timeRecordingsMap.set(RecordingsKeys.older, []);

  recordings.forEach((recording) => {
    // Determining witch key to be used on the map, so a recording can be inserted
    let key = RecordingsKeys.older;

    if (recording.startDate === thisWeekMondayDate || TimeUtils.isAfter(recording.startDate, thisWeekMondayDate)) {
      key = RecordingsKeys.thisWeek;
    } else if (
      (recording.startDate === lastWeekMondayDate || TimeUtils.isAfter(recording.startDate, lastWeekMondayDate)) &&
      TimeUtils.isBefore(recording.startDate, thisWeekMondayDate)
    ) {
      key = RecordingsKeys.lastWeek;
    } else if (recording.startDate === thisMonthDate || TimeUtils.isAfter(recording.startDate, thisMonthDate)) {
      key = RecordingsKeys.thisMonth;
    } else if (
      (recording.startDate === lastMonthDate || TimeUtils.isAfter(recording.startDate, lastMonthDate)) &&
      TimeUtils.isBefore(recording.startDate, thisMonthDate)
    ) {
      key = RecordingsKeys.lastMonth;
    } else if (recording.startDate === twoMonthsAgoDate || TimeUtils.isAfter(recording.startDate, twoMonthsAgoDate)) {
      key = RecordingsKeys.twoMonthsAgo;
    } else if (
      (recording.startDate === threeMonthsAgoMonthDate ||
        TimeUtils.isAfter(recording.startDate, threeMonthsAgoMonthDate)) &&
      TimeUtils.isBefore(recording.startDate, twoMonthsAgoDate)
    ) {
      key = RecordingsKeys.threeMonthsAgo;
    }

    // Inserting the recording in the correct key/value map
    const recordingsFromMap = timeRecordingsMap.get(key);
    if (recordingsFromMap) {
      recordingsFromMap.push(recording);
    }
  });

  const translations: { [key: string]: string } = {
    // Fixed rail titles to be used on the 'all filter'
    [RecordingsKeys.thisWeek]: translate['mytv.recordingsLevel.thisWeek'],
    [RecordingsKeys.lastWeek]: translate['mytv.recordingsLevel.lastWeek'],
    [RecordingsKeys.thisMonth]: translate['mytv.recordingsLevel.thisMonth'],
    [RecordingsKeys.lastMonth]: translate['mytv.recordingsLevel.lastMonth'],
    [RecordingsKeys.older]: translate['mytv.recordingsLevel.olderRecordings'],

    // Dynamic rail titles to be used on the 'all filter'
    [RecordingsKeys.twoMonthsAgo]: TimeUtils.getLongMonth(TimeUtils.toUnixEpoch(twoMonthsAgoDate) * 1000),
    [RecordingsKeys.threeMonthsAgo]: TimeUtils.getLongMonth(TimeUtils.toUnixEpoch(threeMonthsAgoMonthDate) * 1000),
  };

  const recordingsArray: SecondLevelRails[] = Array.from(timeRecordingsMap, ([key, value]) => ({
    railId: key,
    menuItemId,
    railName: translations[key],
    assets: value,
  }));
  const sortedAssets = recordingsArray.filter(hasAssets);

  return sortedAssets;
}

function* getSecondLevelAssetsSaga(action: GetSecondLevelAssetsRequest): SagaIterator {
  const { menuItemId } = action.meta;
  let assets: Asset[] | Recording[];
  let sortedAssets: SecondLevelRails[];
  let badgesPriorities: Array<BadgeType[]> | undefined;

  const myTVMenu: MenuItem = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.MY_TV);
  const menuItem = myTVMenu.children.find((item) => item.id === menuItemId);

  switch (menuItem?.type) {
    case MenuItemType.WISHLIST:
      assets = yield select(WishlistSelectors.getAssets);
      badgesPriorities = BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.WISHLIST_RAIL;
      break;
    case MenuItemType.RENTALS:
      assets = yield select(EntitlesAssetsSelectors.getAssets);
      badgesPriorities = BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RENTAR_AND_PURCHASES_RAIL;
      break;
    case MenuItemType.RECORDINGS:
      yield call(waitForRecordings);
      assets = yield select(MyTvRecordingsSelectors.getRecordings);
      badgesPriorities = BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECORDING_RAIL;
      break;
    case MenuItemType.SCHEDULE_RECORDINGS:
      yield call(waitForRecordings);
      assets = yield select(MyTvRecordingsSelectors.getScheduledRecordings);
      badgesPriorities = BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECORDING_RAIL;
      break;
    default:
      assets = [];
      break;
  }

  if (badgesPriorities) {
    assets = yield call(RailBadgeEngine.getAssetsWithBadgesListFromRail, assets, badgesPriorities);
  }

  if (menuItem?.type === MenuItemType.WISHLIST || menuItem?.type === MenuItemType.RENTALS) {
    sortedAssets = divideByGenre(assets, menuItemId);
  } else {
    sortedAssets = getAllRecordingsMap(assets as Recording[], menuItemId);
  }

  yield put(Actions.getSecondLevelAssetsSuccess(sortedAssets, menuItemId));
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionType.GET_SECOND_LEVEL_ASSETS, getSecondLevelAssetsSaga)]);
}
